/**
 * Los borradores de alta (US5 · FR-022…FR-028).
 *
 * QUÉ ENSEÑA ESTA PANTALLA QUE NO SE VE EN OTRO SITIO
 * ──────────────────────────────────────────────────
 * **Qué le falta a cada uno**, sin abrirlo. Un listado de nombres y fechas
 * obligaría a entrar en los cinco para saber cuál está a punto de poder
 * emitirse, y entonces la lista no sirve para lo único que se usa: decidir cuál
 * retomar.
 *
 * Y lo que falta se calcula con **el mismo validador que rechazaría el alta**,
 * no con una lista aparte de campos obligatorios. Esa lista y el validador de
 * verdad divergen en tres semanas, y entonces la pantalla dice que falta algo
 * que no falta — o peor, dice que no falta nada y el alta muere en la saga.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { useSesion } from '../../auth'
import { Button } from '../../components/ui/button'
import {
    descartarBorradorApi,
    listarBorradoresApi,
    type BorradorResumen,
} from '../../lib/altas'

function cuando(iso: string): string {
    const d = new Date(iso)
    const hoy = new Date()
    const mismoDia = d.toDateString() === hoy.toDateString()
    return mismoDia
        ? `hoy a las ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`
        : d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function Borradores() {
    const { data: sesion } = useSesion()
    const navigate = useNavigate()
    const [filas, setFilas] = useState<BorradorResumen[]>([])
    const [cargando, setCargando] = useState(true)
    // Sin esto, un fallo al pedir la lista se pintaba como «no hay ninguna a
    // medias» — quien tenía cinco altas guardadas concluía que se habían borrado.
    const [error, setError] = useState<string | null>(null)
    const [abierto, setAbierto] = useState<number | null>(null)

    async function recargar() {
        try {
            setFilas(await listarBorradoresApi())
            setError(null)
        } catch {
            setError('No se pudo leer la lista de borradores.')
        } finally {
            setCargando(false)
        }
    }

    useEffect(() => {
        void recargar()
    }, [])

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    async function descartar(id: number, nombre: string) {
        // El texto dice explícitamente que no hay nada fuera que deshacer
        // (FR-026). Es la contrapartida de FR-023 y quita la duda de «¿esto
        // habrá dejado media cuenta creada por ahí?».
        if (!confirm(`¿Descartar «${nombre}»? No existe en ningún otro sitio, así que no deja rastro.`)) return
        try {
            await descartarBorradorApi(id)
        } catch {
            setError('No se pudo descartar. Sigue ahí.')
        }
        void recargar()
    }

    return (
        <div className="mx-auto w-full max-w-[1700px] space-y-6">
            <header className="flex items-start justify-between gap-4">
                <div>
                    <Link to="/altas" className="text-xs text-neutral-500 hover:text-neutral-300">
                        ← Altas
                    </Link>
                    <h1 className="text-lg font-semibold text-neutral-100">Altas a medias</h1>
                    <p className="mt-1 text-sm text-neutral-400">
                        Lo que alguien empezó y dejó para después. Se retoma donde se quedó.
                    </p>
                </div>
                <Link
                    to="/altas/nueva"
                    className="rounded-md bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-white"
                >
                    Nueva alta
                </Link>
            </header>

            <p className="rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-xs text-neutral-400">
                Un borrador <strong>solo existe aquí</strong>: no ha creado ninguna cuenta de canal,
                ninguna agenda ni nada en el asistente. Todo eso pasa al emitirlo, y no antes.
            </p>

            {cargando ? (
                <p className="text-sm text-neutral-500">Cargando…</p>
            ) : error ? (
                /* Un fallo NO se enseña como una lista vacía: son cosas distintas
                   y quien las confunde da por perdido su trabajo. */
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-6 text-center">
                    <p className="text-sm text-rose-300">{error}</p>
                    <p className="mt-1 text-xs text-neutral-500">
                        No quiere decir que no haya ninguna: quiere decir que no se ha podido
                        preguntar.
                    </p>
                </div>
            ) : filas.length === 0 ? (
                <div className="rounded-lg border border-dashed border-neutral-800 p-8 text-center">
                    <p className="text-sm text-neutral-400">No hay ninguna a medias.</p>
                    <p className="mt-1 text-xs text-neutral-500">
                        En el formulario de alta, «Guardar y seguir luego».
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filas.map((b) => (
                        <section
                            key={b.id}
                            className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/40"
                        >
                            <div className="flex items-start gap-3 px-4 py-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-700 bg-neutral-950/60">
                                    {b.estado.completo ? '✅' : '✏️'}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium text-neutral-100">{b.nombre}</span>
                                        {b.estado.completo ? (
                                            <span className="rounded bg-emerald-900/50 px-1.5 py-0.5 text-[10px] text-emerald-300">
                                                se puede emitir
                                            </span>
                                        ) : (
                                            <span className="rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] text-amber-300">
                                                le faltan {b.estado.problemas.length}
                                            </span>
                                        )}
                                        {/* FR-027 · avisa, no rechaza */}
                                        {b.estado.contratoDistinto && (
                                            <span
                                                className="rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] text-amber-300"
                                                title="Se empezó contra otra versión del contrato de la ficha. Revísalo antes de emitirlo."
                                            >
                                                contrato v{b.versionContrato}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-0.5 text-xs text-neutral-500">
                                        {cuando(b.actualizadoEn)}
                                        {b.actualizadoPor ? ` · ${b.actualizadoPor}` : ''}
                                    </p>
                                </div>
                                <div className="flex shrink-0 gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setAbierto(abierto === b.id ? null : b.id)}
                                    >
                                        {abierto === b.id ? 'Ocultar' : 'Qué le falta'}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={() => navigate(`/altas/nueva?borrador=${b.id}`)}
                                    >
                                        Seguir
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => void descartar(b.id, b.nombre)}
                                    >
                                        Descartar
                                    </Button>
                                </div>
                            </div>

                            {abierto === b.id && (
                                <div className="border-t border-neutral-800 bg-neutral-950/40 px-4 py-3">
                                    {b.estado.contratoDistinto && (
                                        <p className="mb-3 rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
                                            Este borrador se empezó contra la versión{' '}
                                            <strong>{b.versionContrato}</strong> del contrato de la ficha, y
                                            ya no es la vigente. Puede que algo que entonces valía ahora no
                                            valga. Se avisa al abrirlo, no al emitirlo.
                                        </p>
                                    )}
                                    {b.estado.completo ? (
                                        <p className="text-xs text-emerald-300">
                                            No le falta nada: se puede emitir tal cual.
                                        </p>
                                    ) : (
                                        <ul className="space-y-1">
                                            {b.estado.problemas.map((p, i) => (
                                                <li key={i} className="text-xs text-neutral-300">
                                                    <code className="text-neutral-500">{p.campo}</code>{' '}
                                                    {p.mensaje ?? p.motivo}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </section>
                    ))}
                </div>
            )}
        </div>
    )
}
